import { classifyConsumerDevice, consumerSymbolChannelType } from "./ConsumerDeviceKind";
import type { C1_Consumer } from "../../ConceptOne/src/model/C1_Consumer";
import { migrateConsumerDetails, migratedDoorEquipmentChannelType } from "./consumerChannelMigration";
import { copyAllProperties, later, sortArrayByIntProperty, sortArrayByProperty } from "./utils";
import Connection, { ConnectionType } from "./Connection";
import BaseObject from "./BaseObject";
import ConsumerTreeViewModel from "../modelTreeViews/ConsumerTreeviewModel";
import { ChannelType, channelTypeFromString, channelTypeInfo, getChannelTypePicture, isSameLightRolloHeating,
  PLUG_CHANNEL_TYPES, isPlugChannelType, isSwitchedPlugChannelType, isCablelessPlugChannelType, electricalChannelType } from "./ChannelType";
import ConnectableObject from "./ConnectableObject";
import type Page from "./Page";
import BaseModel, { executePaste_Parameters } from "./BaseModel";
import type Room from "./Room";
import GroupAddress from "./GroupAdress";
import BaseModelMap from "./BaseModelMap";
import Signal from "./Signal";
import WireEndpoint, { calculateWireEndpoints } from "./WireEndpoint";
import SignalType from "./SignalType";

import Logger from "../Logger";
const logger = new Logger();

import Fuse from "./Fuse";
import { DragMode, DropResult } from "./TreeviewModel";
import Terminal from "./Terminal";
import TerminalStrip from "./TerminalStrip";
import Multiplexer from "./Multiplexer";
import AllowedConnections from "./AllowedConnections";
import { CableType } from "./CableType";
import Cabinet from "./Cabinet";
import Actor from "./Actor";
import { estadaConfirm } from "../modelDialogs/ConfirmDialog";
import { itx, noItx } from "../translationItx/itx";

import type Floor from "./Floor";

//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import { showErrToast } from "../components/Toasts";
import Button from "./Button";
import TerminalBlock from "./TerminalBlock";
import AllowedConections from "./AllowedConnections";
import ButtonChannel from "./ButtonChannel";
import RelationConsumerToTerminal from "./RelationConsumerToTerminal";
import RelationConsumerToFuse from "./RelationConsumerToFuse";
import { isUiModeProductAdmin } from "../uiMode";
import SignalTypes from "./SignalTypes";
import Channel from "./Channel";

ObjectFactory.registerClass("Consumer", (data?: any, parent?: BaseModel) => {
  return new Consumer(data, parent);
});
//////////////////////////////////////////////////

export type WireRequirement = {
  numberOfLWiresWithoutPEandN: number;
  functionOfWires: string[];
  voltage: TerminalStripVoltageLevel;
};

export enum TerminalStripVoltageLevel {
  V230 = "230V", //no itx
  V12 = "12V", // no itx
  V24 = "24V", // no itx
  Digital = "Digital", // no itx
  Undefined = "Undefined", // no itx
}

export default class Consumer extends ConnectableObject {
  public get className(): string {
    return "Consumer"; // no itx
  }

  getParentCollectionName(): string | undefined {
    return "consumers"; // no itx
  }

  /**
   * Stabile ID eines anderen Consumer-Objekts, mit dem dieser Verbraucher
   * physisch gemeinsam verdrahtet und gemeinsam geschaltet wird.
   *
   * Concept One erzeugt diese Referenz beim ESTADA-Export fÃ¼r Leuchtengruppen:
   * Die erste Leuchte der Gruppe ist das fÃ¼hrende Objekt und besitzt keinen
   * Wert. Jede weitere Leuchte (Tags z. B. L06a, L06b, â€¦) verweist auf die ID
   * dieser ersten Leuchte (z. B. des Objekts mit Tag L06). Verwendet wird die
   * Objekt-ID und nicht der Tag, weil Tags vom Benutzer geÃ¤ndert oder neu
   * generiert werden kÃ¶nnen.
   *
   * Die Property fasst die Objekte nicht zusammen: Jede Leuchte bleibt ein
   * eigener Consumer mit eigener ID, Position, Darstellung und Dokumentation.
   * Sie teilt ESTADA lediglich mit, dass fÃ¼r die Folge-Leuchte kein unabhÃ¤ngiger
   * Leitungsweg beziehungsweise Schalt-/Dimmkanal geplant werden soll. Die
   * Referenz ist absichtlich gerichtet und nicht transitiv: Alle Folgeobjekte
   * zeigen direkt auf dasselbe erste Objekt.
   */
  wiredTogetherWith?: string;

  _groupAddressesId?: string;
  get groupAddress(): GroupAddress | undefined {
    return this.state.objectRegistry.getObject(this._groupAddressesId);
  }

  set groupAddress(newValue: GroupAddress | undefined) {
    if (this._groupAddressesId === newValue?._id) return;
    this.beforeChange("groupAddress");
    this._groupAddressesId = newValue?._id;
    this.changed("groupAddress");
  }

  // -----------------------------------------------------------------------------
  // prefixType
  // -----------------------------------------------------------------------------
  _prefixType: ChannelType | undefined;

  get prefixType(): ChannelType {
    return this._prefixType || this.channelType;
  }

  set prefixType(newValue: ChannelType) {
    this._prefixType = newValue;
  }

  // -----------------------------------------------------------------------------

  updatePrefixTypeFromImage() {
    if (isPlugChannelType(this.channelType)) {
      this.prefixType = this.channelType;
      return;
    }
    switch (this.image) {
      case state.hostBaseUrl + "images/consumer/PlugSwitchable.png": {
        this.channelType = this.prefixType = ChannelType.plug_Switch;
        break;
      }
      case state.hostBaseUrl + "images/consumer/Plug.png": {
        this.channelType = this.prefixType = ChannelType.plug_230V;
        break;
      }
    }
  }

  public get isConsumer(): boolean {
    return true;
  }
  get errorText(): string {
    let error = this._errorText;
    if (error == "" && this.channelType == ChannelType.Unknown) {
      error = "channeltype not set";
    }
    return error || "";
  }
  set errorText(newValue: string) {
    this._errorText = newValue;
  }

  /** Migrates the old C1 function before the owner's persistent properties are restored. */
  override fromPersistentData(data: any): any {
    const doorChannelType = migratedDoorEquipmentChannelType(data.c1?._defId ?? data.c1?.definitionId);
    if (
      doorChannelType &&
      (!data.channelType || data.channelType === ChannelType.Switch || data.channelType === ChannelType.Unknown)
    ) {
      data = { ...data, channelType: doorChannelType };
    }
    const legacyDetails = data.c1?.details;
    if (legacyDetails?.lightType) {
      const details = migrateConsumerDetails(legacyDetails);
      const { channelType, isDali, ...facetDetails } = details;
      data = { ...data, channelType, _isDali: isDali, c1: { ...data.c1, details: facetDetails } };
    }
    return super.fromPersistentData(data);
  }

  constructor(data?: any, parent?: BaseModel) {
    super(data, parent);
    copyAllProperties(data, this);
    this.treeViewModel = new ConsumerTreeViewModel(this);
  }

  signals = new BaseModelMap<Signal>(Signal, this, "s");

  wireEndpoints?: BaseModelMap<WireEndpoint>;

  /** Refreshes the derived conductor junctions used by the connection analysis. */
  calcWireEndpoints(): void {
    this.wireEndpoints = calculateWireEndpoints(this);
  }

  get isSubDistribution(): boolean {
    return this.wireEndpoints !== undefined;
  }

  /////////////////////////////////////////////////////////////////////////
  //
  //  Relation with other Objects
  //
  /////////////////////////////////////////////////////////////////////////

  //
  // Fuse
  //

  _fuseRelation = new RelationConsumerToFuse(this);

  //
  // fuseId soll nur für die Serialisierung verwendet werden
  //
  public get fuseId(): string | undefined {
    return this._fuseRelation.toId;
  }

  public set fuseId(value: string | undefined) {
    this.beforeChange("fuseId");
    this._fuseRelation.toId = value;

    this.changed("fuseId");
  }

  get fuse(): Fuse | undefined {
    if (this.noCable) {
      return undefined;
    }
    return this._fuseRelation.to;
  }

  set fuse(newValue: Fuse | undefined) {
    if (this._fuseRelation.to === newValue) return;
    this.beforeChange("fuseId");
    this._fuseRelation.to = newValue;
    this.changed("fuseId");
  }

  /////////////////////////////////////////////////////////////////////////

  //
  // Terminal
  //

  _terminalRelation = new RelationConsumerToTerminal(this);

  //
  // terminalId soll nur für die Serialisierung verwendet werden
  //
  public get terminalId(): string | undefined {
    return this._terminalRelation.toId;
  }

  public set terminalId(value: string | undefined) {
    this.beforeChange("terminalId");
    this._terminalRelation.toId = value;

    this.changed("terminalId");
  }

  ////////////////////////////////////////////////////

  get terminal(): Terminal | undefined {
    return this._terminalRelation.to;
  }

  set terminal(newValue: Terminal | undefined) {
    if (this._terminalRelation.to === newValue) return;
    this.beforeChange("terminalId");
    this._terminalRelation.to = newValue;

    //
    // wenn diesem Consumer ein Terminal zugeordnet wird,
    // dann müssen wir für alle "eingegebden" viaConsumer den backPointer (_consumer) am Terminal setzen
    //
    this.getAllViaConsumers().forEach((consumer) => {
      consumer.updateTerminalRelationOverViaConsumer();
    });
    this.changed("terminalId");
  }

  //
  // Wenn dieser Consumer über einen viaConsumer mit einem Terminal verbunden ist,
  // muss der pointer vom Consumer zum Terminal und backPointer am Terminal aktualisiert werden
  //
  updateTerminalRelationOverViaConsumer() {
    const viaConsumer = this.getTerminalViaConsumer();
    if (viaConsumer) {
      console.log("updateRelationWithViaConsumer set subTerminal.consumer", "via:", viaConsumer.tag);

      const terminalBlock = viaConsumer.terminal?.getTerminalBlock();
      if (terminalBlock) {
        const terminalIndex = terminalBlock.indexOfTerminalfromWireIndex(this.terminalViaWireIndex || 3);
        const subTerminal = terminalBlock.terminalFromIndex(terminalIndex);

        if (this._terminalRelation.to !== subTerminal) {
          this.beforeChange("terminalId");
          this._terminalRelation.to = subTerminal;
          this.changed("terminalId");
        }
      }
    }
  }

  // Initialize backpointers for all relations
  initRelations() {
    super.initRelations();
    //
    // die direkt Verbinung von Consumer zu Terminal initialisieren
    //
    this._terminalRelation.initRelation();

    //
    // die Verbinung von Consumer zu Fuse initialisieren
    //
    this._fuseRelation.initRelation();

    //
    // die INDIREKTE Verbindung von Consumer zu Terminal über einen viaConsumer initialisieren
    //
    this.updateTerminalRelationOverViaConsumer();
  }

  /////////////////////////////////////////////////////////////////////////

  get terminalBlock(): TerminalBlock | undefined {
    return this.terminal?.getTerminalBlock();
  }

  getIncommingConsumerWithWireIndex(wireIndex: number): Consumer | undefined {
    for (const consumer of this.getAllViaConsumers()) {
      if (consumer.terminalViaWireIndex === wireIndex) {
        return consumer;
      }
    }
    return undefined;
  }
  /////////////////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // noCable
  // -----------------------------------------------------------------------------
  _noCable: boolean | undefined = undefined;

  get noCable(): boolean {
    return isCablelessPlugChannelType(this.channelType) || (this._noCable !== undefined ? this._noCable : this._isDali);
  }

  set noCable(newValue: boolean) {
    this.beforeChange("noCable");
    this._noCable = newValue;

    this.changed("noCable");
  }

  // -----------------------------------------------------------------------------
  // cableTypeId
  // -----------------------------------------------------------------------------
  _cableTypeId?: undefined | string;
  get cableTypeId(): undefined | string {
    return this._cableTypeId;
  }

  set cableTypeId(newValue: undefined | string) {
    if (this._cableTypeId === newValue) return;
    this.beforeChange("cableTypeId");
    this._cableTypeId = newValue;
    this.changed("cableTypeId");
  }

  // -----------------------------------------------------------------------------
  get defaultCableTypeTag(): undefined | string {
    if (this.noCable) {
      return undefined;
    }
    if (isSwitchedPlugChannelType(this.channelType)) {
      return state.settings.cableTypeDefaultForConnectableObjs["PlugSwitchable"];
    }
    return state.settings.cableTypeDefaultForConnectableObjs[this.channelType === ChannelType.plug_230V ? ChannelType.Plug : electricalChannelType(this.channelType)];
  }

  get cableType(): CableType | undefined {
    if (this.noCable) {
      return undefined;
    }
    if (this.cableTypeId) {
      return this.state.objectRegistry.getObject(this.cableTypeId);
    } else {
      let defaultCableType: CableType | undefined;
      defaultCableType = this.getIsDefaultForConsumer();
      // state.cableTypes.forEach((cableType) => {
      //   if (cableType.getIsDefaultForConsumer(this)) {
      //     defaultCableType = cableType;
      //   }
      //   // if (cableType.isDefault) {
      //   //   defaultCableType = cableType;
      //   // }
      // });

      return defaultCableType;
    }
  }

  set cableType(newValue: CableType | undefined) {
    this.cableTypeId = newValue?.id;
  }
  //
  getIsDefaultForConsumer(): CableType | undefined {
    // Steckdosen: NYM-J 5x2,5 mm² (auch schaltbare)-
    //   Licht: NYM-J 5x1,5 mm² (Alles Typen Licht)-
    //   Verbraucher 400V: NYM-J 5x2,5 mm² -
    //   Verbraucher 230V: NYM-J 5x1,5 mm² -
    //   Rollläden/Jalousien: NYM-J 5x1,5 mm²-
    //   Kontakt: YCYM Y(St)Y2x2x0,8 (lila)
    //   Heizung: -
    //
    let cableTypeFound: CableType | undefined;
    state.cableTypes.forEach((cableType) => {
      if (this.defaultCableTypeTag == cableType.id) {
        cableTypeFound = cableType;
      }
      if (this.channelType === ChannelType.plug_230V || isSwitchedPlugChannelType(this.channelType) || this.channelType == ChannelType.suply400V) {
        if (cableType.tag == "C5.25" && cableTypeFound == undefined) {
          cableTypeFound = cableType;
        }
      } else if (
        channelTypeInfo(this.channelType).isLight ||
        this.channelType == ChannelType.suply230V ||
        this.channelType == ChannelType.IntermediateDistribution ||
        channelTypeInfo(this.channelType).isRollo
      ) {
        if (cableType.tag == "C5.15" && cableTypeFound == undefined) {
          cableTypeFound = cableType;
        }
      } else if (this.channelType == ChannelType.Lock) {
        if (cableType.tag == "Cy4.08" && cableTypeFound == undefined) {
          cableTypeFound = cableType;
        }
      } else if (electricalChannelType(this.channelType) == ChannelType.Lan2) {
        // dedicated dual-LAN cable, since a Lan2 consumer occupies 2 actor-channel ports
        if (cableType.tag == "CLan2" && cableTypeFound == undefined) {
          cableTypeFound = cableType;
        }
      } else if (channelTypeInfo(this.channelType).isLan) {
        if (cableType.tag == "CLan" && cableTypeFound == undefined) {
          cableTypeFound = cableType;
        }
      } else if (this.channelType == ChannelType.Heating) {
        if (cableType.tag == "C0" && cableTypeFound == undefined) {
          cableTypeFound = cableType;
        }
      }
    });

    return cableTypeFound;
  }

  //////////////////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // wireRequirement
  // -----------------------------------------------------------------------------
  _wireRequirement: WireRequirement | undefined;

  get wireRequirement(): WireRequirement {
    return this._wireRequirement || this.getDefaultWireRequirement();
  }

  set wireRequirement(newValue: WireRequirement) {
    this._wireRequirement = newValue;
  }

  // -----------------------------------------------------------------------------

  private getDefaultWireRequirement(): WireRequirement {
    let numberOfLWiresWithoutPEandN = 0;
    let voltage = TerminalStripVoltageLevel.Undefined;
    let functionOfWires: string[] = [];

    //// number of wires
    switch (electricalChannelType(this.channelType)) {
      case ChannelType.suply230V:
      case ChannelType.Switch:
      case ChannelType.StaircaseLight:
      case ChannelType.Dimmer:
      case ChannelType.Plug:
      case ChannelType.PlugSwitchable:
      case ChannelType.Heating:
        numberOfLWiresWithoutPEandN = 1;
        functionOfWires = ["L"];
        break;
      case ChannelType.Jalousie:
      case ChannelType.Blind:
        //
        // 🔸 Für Auf/Ab haben sich Schwarz/Braun etabliert
        //
        // Scharz = WireIndex 3/ (1 without PE and N)
        // Braun = WireIndex 2/ (0 without PE and N)
        //
        // (Quasi-Standard), optional Grau als Dauerphase.
        // Wird lt ChatGpt von vielen Herstellern verwendet
        // (z. B. Somfy, Becker, Elero, Gira, Busch-Jäger)
        //
        // MWE 13.10.2025
        //

        functionOfWires = ["Down", "Up"]; // no itx
        numberOfLWiresWithoutPEandN = 2;
        break;

      case ChannelType.TuneableWhite:
        functionOfWires = ["C", "W"];
        numberOfLWiresWithoutPEandN = 2;
        break;

      case ChannelType.Rgb:
        functionOfWires = ["R", "G", "B"];
        numberOfLWiresWithoutPEandN = 3;
        break;

      case ChannelType.suply400V:
        functionOfWires = ["Phase 1", "Phase 2", "Phase 3"]; // no itx
        numberOfLWiresWithoutPEandN = 3;
        break;
    }

    // voltage
    switch (electricalChannelType(this.channelType)) {
      case ChannelType.suply230V:
      case ChannelType.Switch:
      case ChannelType.StaircaseLight:
      case ChannelType.Dimmer:
      case ChannelType.Jalousie:
      case ChannelType.Blind:
      case ChannelType.Plug:
      case ChannelType.PlugSwitchable:
      case ChannelType.suply400V:
      case ChannelType.Heating:
        voltage = TerminalStripVoltageLevel.V230;
        break;
      case ChannelType.suply24V:
      case ChannelType.Rgb:
      case ChannelType.TuneableWhite:
        voltage = TerminalStripVoltageLevel.V24;
        break;
      case ChannelType.Lock:
        voltage = TerminalStripVoltageLevel.Digital;
        break;
    }

    return {
      numberOfLWiresWithoutPEandN,
      functionOfWires,
      voltage,
    };
  }

  fullWireIndex(wireIndexWithoutPEandN: number): number {
    if (wireIndexWithoutPEandN >= 0) {
      if (this.cableType && !this.cableType.hasGround) {
        return wireIndexWithoutPEandN + 1; // +1 da die erste Ader N ist
      } else {
        return wireIndexWithoutPEandN + 2; // +2 da die ersten 2 Adern PE und N sind
      }
    }

    return -1;
  }

  /////////////////////////////////////////////////////////////////////////

  getTerminalViaConsumer(): Consumer | undefined {
    const res = this.getTerminalViaConnection()?.toDevice;
    if (res) {
      if (!res.isConsumer) {
        logger.error("getTerminalViaConsumer: not a consumer. fromConsumer: ", this.fullTag, res);
        return undefined;
      }
    }
    return res as Consumer;
  }

  setTerminalViaConsumer(newValue: Consumer | undefined): Connection | undefined {
    // Consumers can belong to nested equipment groups rather than directly to a room.
    const page = newValue ? this.parentRoom?.getOrCreatePage() : undefined;
    if (newValue && !page) return undefined;
    const c = this.getTerminalViaConnection();
    let oldViaConsumer = this.getTerminalViaConsumer();
    if (c) {
      c.delete();
    }

    if (newValue) {
      if (page) {
        const neededNumberOfWires = this.wireRequirement?.numberOfLWiresWithoutPEandN || 1;
        const freeWireIndexs =
          newValue?.findFreeWireIndexs(neededNumberOfWires, this)[0] || newValue?.cableType?.numberOfWires || 3;
        const connection = new Connection({ tag: freeWireIndexs.toString() }, page);
        page.connections.add(connection);

        connection.type = ConnectionType.TerminalVia;
        connection.fromDevice = this;
        connection.toDevice = newValue;

        connection.addToConnectableObjects();
        connection.updateConnectedPartners();

        this.terminalViaWireIndex = freeWireIndexs;

        if (this.isConsumer && newValue.isConsumer && this.terminal && this.terminal.isMasterTerminal) {
          // terminal will be handeled by targetConsumer
          const sourceConsumerTerminal = this.terminal;
          this.terminal = undefined;
          sourceConsumerTerminal?.raiseOnChange();
          if (sourceConsumerTerminal) {
            sourceConsumerTerminal.delete();
          }
          const targetConsumerTerminal = newValue?.terminal;
          if (targetConsumerTerminal) {
            targetConsumerTerminal.splitIntoSingleTerminals();
            setTimeout(() => {
              targetConsumerTerminal.raiseOnChange();
              targetConsumerTerminal.parent?.raiseOnChange();
            }, 1000);
          }
        }
        // check if cable type of viaConsumer has additional wires
        const cableType = newValue?.cableType;
        const freeWiresWithOwn = newValue.findFreeWireIndexs(neededNumberOfWires, this);
        if (cableType && freeWiresWithOwn.length < neededNumberOfWires) {
          showErrToast(
            itx(
              "Consumer.viaConsumerhatnichtgenugadern",
              "Der Unterverteiler hat nicht genug Adern für diese Verbindung.",
            ),
          );
        }

        return connection;
      }
    }
    // update terminal
    try {
      newValue?.initRelations();
      this?.initRelations();
      oldViaConsumer?.initRelations();
    } catch (ex) {
      logger.error("Error in initRelations", ex);
    }
    return undefined;
  }

  getTerminalViaConnection(): Connection | undefined {
    for (const [key, connection] of this.connections.items) {
      // console.log("getTerminalViaConnection", connection.type, connection.toDevice?.tag);
      if (connection.type == ConnectionType.TerminalVia) {
        if (connection.fromDevice == this && connection.fromDevice.isConsumer) {
          return connection;
        }
      }
    }

    return undefined;
  }

  getIncommingTerminalViaConsumers(): Array<Consumer> {
    const res = new Array<Consumer>();

    for (const [key, connection] of this.connections.items) {
      if (connection.type == ConnectionType.TerminalVia && connection.toDevice == this) {
        res.push(connection.fromDevice as Consumer);
      }
    }

    return sortArrayByProperty(res, "fullTag");
  }

  //
  // ist gleichbedeutend mit "isJunctionBox" / "ist Unterverteiler"
  get isViaConsumer(): boolean {
    return this.getIncommingTerminalViaConsumers().length > 0;
  }

  get terminalViaWireName(): string {
    const idx = this.terminalViaWireIndex;
    const cableType = this.getTerminalViaConsumer()?.cableType;
    if (cableType && idx !== undefined && idx >= 0 && idx < cableType.numberOfWires) {
      return cableType.wireDefinitions[idx]?.longName || "?";
    }

    return "Ader" + " " + (idx !== undefined ? idx : "?");
  }

  get terminalViaWireIndex(): number | undefined {
    const connection = this.getTerminalViaConnection();
    if (connection) {
      if (connection.tag) {
        return parseInt(connection?.tag);
      }

      //
      // for backward compatibility
      //
      const name = connection.name.split("?")[0];
      if (name.startsWith("sw")) {
        return 4;
      } else {
        return 3;
      }
    }

    return undefined;
  }

  set terminalViaWireIndex(newValue: number) {
    const c = this.getTerminalViaConnection();
    if (c) {
      c.tag = `${newValue}`;
    }

    this.updateTerminalRelationOverViaConsumer();
  }

  ///////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // quantity
  // -----------------------------------------------------------------------------
  _quantity: number = 1;

  get quantity(): number {
    return this._quantity;
  }
  set quantity(newValue: number) {
    this.beforeChange("quantity");
    if (newValue > 0) {
      this._quantity = newValue;
    }

    this.changed("quantity");
  }

  // -----------------------------------------------------------------------------
  /////////////////////////////////////////////////////////////////////////

  onSelect(newValue: boolean) {
    this.updateConnectionEndpoints();
  }

  getPersistentProperties(): string[] {
    return [
      "signals", // no itx
      "image", // no itx
      "fuseId", // no itx
      "terminalId", // no itx
      "_quantity", // no itx
      "cableTypeId", // no itx
      "_noCable", // no itx
      "_groupAddressesId", // no itx
      "wiredTogetherWith", // no itx: stable ID of the leading jointly wired consumer
      ...super.getPersistentProperties(),
    ];
  }

  getGroupAddressByType(type: SignalType) {
    var res: GroupAddress | undefined;
    const signal = this.getSignalByType(type);
    res = signal?.groupAddresses.asArray()[0];
    return res;
  }

  getSignalByType(type: SignalType): Signal | undefined {
    return Consumer.getSignalByType(this, type);
  }

  getOrCreateSignalByType(type: SignalType): Signal | undefined {
    return Consumer.getOrCreateSignalByType(this, type);
  }

  getChangableChannelType(): Array<string> {
    const res = new Array<string>();
    if (isUiModeProductAdmin()) {
      for (const i in ChannelType) {
        res.push(i);
      }
    } else if (isPlugChannelType(this.channelType)) {
      res.push(...PLUG_CHANNEL_TYPES);
    } else if (channelTypeInfo(this.channelType).isLight) {
      for (const i in ChannelType) {
        if (channelTypeInfo(channelTypeFromString(i)).isLight) {
          res.push(i);
        }
      }
    } else if (channelTypeInfo(this.channelType).isRollo) {
      for (const i in ChannelType) {
        if (channelTypeInfo(channelTypeFromString(i)).isRollo) {
          res.push(i);
        }
      }
    } else if (channelTypeInfo(this.channelType).isHeating) {
      res.push(ChannelType.Heating);
    }
    return res;
  }

  /** Applies DALI wiring rules consistently in both application editors. */
  changeDali(value: boolean): void {
    if (this.isDali === value) return;
    this.isDali = value;
    if (value) {
      this.fuse?.raiseOnChange("consumers");
      this.fuse = undefined;
      this.raiseOnChange("fuse");
      this.connectWithTerminal(undefined);
      this.raiseOnChange("terminal");
    }
    for (const connection of this.connections) {
      if (connection.type === ConnectionType.Channel) {
        connection.checkForbidden();
        connection.raiseOnChange();
      }
    }
    this.raiseOnChange();
  }

  changeChannelType(channelType: string) {
    const newChannelType = channelTypeFromString(channelType);
    const wasCableless = isCablelessPlugChannelType(this.channelType);
    this.channelType = newChannelType;
    this.prefixType = newChannelType;
    if (wasCableless || isCablelessPlugChannelType(newChannelType)) this.noCable = isCablelessPlugChannelType(newChannelType);
    // handle plugSwitchable

    const oldPicture = this.image;
    this.image = "";

    //cut connection to actor// and depends to scene

    this.connections.forEach((con) => {
      const channel = con.getChannel();
      if (channel) {
        con.delete();
      }
      const scene = con.getScene();
      if (channelTypeInfo(this.channelType).isSupply && scene) {
        con.delete();
      }
    });

    //search Actorchannel with same channelType and connect

    const page = this.room?.page;
    if (page) {
      var undoObjects = new Array<BaseModel>();
      undoObjects = this.autoConnectChannel(page, undefined, this.isDali);

      undoObjects.forEach((item) => {});
    }
  }

  getParentChildrenGroup(): Array<BaseModel> | undefined {
    return (this.parent as Room)?.consumers.sortBy("tag");
  }

  isConnectable(
    partnerConnectionType: ConnectionType,
    partnerObject: ConnectableObject,
    checkForConnection?: Connection,
  ): boolean {
    if (isCablelessPlugChannelType(this.channelType)) return false;
    if ((partnerObject.className === "Button" || partnerObject.isButtonChannel) &&
      !channelTypeInfo(this.channelType).canBeConnectedWithButtonChannel) return false;
    if (partnerObject.className == "Button") {
      // Ein Button kann immer verbunden werden
      // Es wird dann ein neuer ButtonChannel angelegt
      return true;
    }

    const allowedCallNameAndChanneltypeCombinations = [
      {
        a_this: ChannelType.Blind,
        b_other: ChannelType.Jalousie,
      },
      {
        a_this: ChannelType.Jalousie,
        b_other: ChannelType.Blind,
      },
      {
        a_this: ChannelType.Dimmer,
        b_other: ChannelType.Switch,
      },
      {
        // a Lan2 consumer (e.g. a double LAN socket) connects to a regular Lan actor channel
        a_this: ChannelType.Lan2,
        b_other: ChannelType.Lan,
      },
    ];

    switch (partnerConnectionType) {
      case ConnectionType.Channel:
        // for reconnecting with other channel ( existin channel-connection will be deleted if new channel-connection is created)
        //if (!checkForConnection && this.isConnected(ConnectionType.Channel)) {
        //   return false;
        // }
        let res = electricalChannelType(partnerObject.channelType) == electricalChannelType(this.channelType);
        allowedCallNameAndChanneltypeCombinations.forEach((comb) => {
          if (comb.a_this == electricalChannelType(this.channelType) && comb.b_other == electricalChannelType(partnerObject.channelType)) {
            res = true;
          }
        });
        return res;

      case ConnectionType.Button: {
        if (channelTypeInfo(this.channelType).isLan) {
          // LAN consumers cannot be connected to buttons
          return false;
        }
        if (partnerObject.channelType == ChannelType.Generic) {
          return true;
        }

        //
        // Button Tasten "Scene" können NIE mit einem Consumer verbunden werden
        //
        if (partnerObject.channelType == ChannelType.Scene) {
          return false;
        }

        if (this.channelType == ChannelType.Heating) {
          //
          // Ein HeizungsConsumer darf mit einer Temeperatur-Messung + einer Temperatur-Einstellung verbunden werden
          //
          return channelTypeInfo(partnerObject.channelType).temperatureAndHeatingChannelTypes;
        }
        let res = electricalChannelType(partnerObject.channelType) == electricalChannelType(this.channelType);
        allowedCallNameAndChanneltypeCombinations.forEach((comb) => {
          if (comb.a_this == electricalChannelType(this.channelType) && comb.b_other == electricalChannelType(partnerObject.channelType)) {
            res = true;
          }
        });

        //
        // Für andere Button Tasten muß der ChannelType (Switch/Dim/TuneableWhite) übereinstimmen
        //
        return res;
      }
      case ConnectionType.Multiplexer: {
        if (channelTypeInfo(this.channelType).isLan) {
          // LAN consumers cannot be connected to Multiplexers
          return false;
        }
        // Ein multiplexer kann mit passenden channelTypes verbunden werden
        if (isSameLightRolloHeating(this.channelType, partnerObject.channelType)) {
          return true;
        }
      }

      case ConnectionType.Scene: {
        if (channelTypeInfo(this.channelType).isLan) {
          // LAN consumers cannot be connected to scenes
          return false;
        }
        // avoid double connections with the same scene
        // for now we are missing the target object for this
        if (partnerObject) {
          let res = true;
          if (!checkForConnection) {
            this.connections.forEach((connection) => {
              if (connection.getPartner(this) == partnerObject) {
                // consumer is already conected with this scene
                res = false;
              }
            });
          }
          return res;
        } else {
          logger.error("parameter partnerObject not set");
        }
      }

      case ConnectionType.TerminalVia: {
        if (channelTypeInfo(this.channelType).isLan) {
          // LAN consumers cannot be connected to TerminalVia
          return false;
        }
      }

      default: {
        logger.error(`unknown Type`);
        return false;
      }
    }

    return true;
  }

  moveToNewParent(newParent: BaseModel): boolean {
    if (newParent.className == "Floor") {
      newParent = (newParent as Floor).getRoomGeneral();
    }

    if (newParent.className == "Room" || newParent.isGroup) {
      // const oldCabinet = this.parentRoom?.cabinet;

      let moved = super.moveToNewParent(newParent);
      const newPage = newParent.isRoom
        ? (newParent as Room).getOrCreatePage()
        : newParent.parentRoom?.getOrCreatePage();
      if (!newPage) return moved;

      //
      // move all connections to new page
      //
      this.connections.forEach((connection) => {
        moved = connection.moveToNewParent(newPage) || moved;
      });

      //////////////////////////////////////
      // consumer bleibt mit seinem channel verbunden, egal ob es ein neuer Schaltschrank ist

      //const newCabinet = this.parentRoom?.cabinet;

      // if (newCabinet == oldCabinet) {
      //   //
      //   // der Conumer wurde in ein Raum verschoben der dem gleichen Cabinet zugeordnet ist.
      //   // Somit können alle Verbindung beibehalten werden.
      //   // Die Verbindungen wurden oben bereits zu der neuen page geschoben
      //   //
      // } else {
      //   //
      //   // der Conumer wurde in ein Raum verschoben der einem anderen Cabinet zugeordnet ist.
      //   // Somit kann die Verbindung zum TerminalStrip als auch zum Aktor nicht beibehalten werden.
      //   //
      //   // die Verbindung des Consumers zum Aktor wird gelöst (sofern vorhanden)
      //   // anschließend wird mit autoConnectChannel versucht
      //   // eine neue Verbindung zu einen passenden Aktor im neuen Schaltschrank herzustellen
      //   //
      //   // const channel = this.getConnectedChannel();
      //   // if (channel) {
      //   //   const connection = this.getConnectionWith(channel);
      //   //   if (connection) {
      //   //     connection.delete();
      //   //   }
      //   //   this.autoConnectChannel(newPage);
      //   //   //
      //   //   // Die Verbindung zum TerminalStrip wird gelöst.
      //   //   //
      //   //   // Es wird kein neuer TerminalStrip gesucht,
      //   //   // da es nicht allgemein möglich ist einen TerminalStrip automatisch zu ermitteln
      //   //   //
      //   //   this.terminal = undefined;
      //   // }
      // }

      return moved;
    } else {
      return false;
    }
  }

  set image(newValue: string | undefined) {
    this.beforeChange("image");
    if (newValue === undefined) {
      newValue = "";
    }
    this._image = newValue;

    this.changed("image");
  }

  get image(): string {
    if (this._image != "") {
      let res = this._image;
      if (this.isDali && !res.includes("_Dali")) {
        const index = res.lastIndexOf(".");
        if (index !== -1) {
          res = res.substring(0, index) + "_Dali" + res.substring(index);
          this._image = res;
        }
      } else if (!this.isDali && res.includes("_Dali")) {
        res = res.replace("_Dali", "");
        this._image = res;
      }

      return res;
    }

    const symbolChannelType = consumerSymbolChannelType({channelType: this.channelType, prefixType: this.prefixType, image: this._image, c1: this.c1});
    switch (symbolChannelType) {
      case ChannelType.Switch:
        if (this.tag.includes(state.settings.consumerPrefix.switchOutlet)) {
          return state.hostBaseUrl + "images/consumer/PlugSwitchable.png";
        }
        break;

      case ChannelType.PlugSwitchable:
        return state.hostBaseUrl + "images/consumer/PlugSwitchable.png";

      case ChannelType.Plug:
        return state.hostBaseUrl + "images/consumer/Plug.png";

      case ChannelType.suply230V:
        if (this.tag.includes(state.translate("toolBar.switchOutletShortTag", "P"))) {
          return state.hostBaseUrl + "images/consumer/Plug.png";
        }
        break;
    }

    const image = getChannelTypePicture(symbolChannelType as ChannelType);
    // add dali image if isDali
    if (this.isDali) {
      const index = image.lastIndexOf(".");
      if (index !== -1) {
        const daliImage = image.substring(0, index) + "_Dali" + image.substring(index);
        return daliImage;
      }
    }
    return image;
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;

    var res = new Array<BaseObject>();

    //
    // if a consumer uses "this" as TerminalViaConsumer modify the incoming consumer
    //
    this.getIncommingTerminalViaConsumers().forEach((consumer) => {
      consumer.setTerminalViaConsumer(undefined);
    });

    // fuse
    this.fuse = undefined;

    // delete all connections from/to this consumer
    res = res.concat(this.connections.deleteAllItems());

    //
    // delete all GroupAddresses genereated from this consumer
    //
    // this.signals.forEach((signal) => {
    //   signal.groupAddresses.asArray().forEach((groupAddress) => {
    //     res = res.concat(groupAddress.delete())
    //   });
    // });

    res = res.concat(super.delete());
    return res;
  }

  ////////////////////////////////////////////////////////////////////
  canBeEditedInteractive(): boolean {
    return false;
  }

  ////////////////////////////////////////////////////////////////////////////////
  autoConnectChannel(page: Page, parent?: Cabinet, isDali?: boolean): Array<BaseModel> {
    if (this.channelType === ChannelType.IntermediateDistribution || isCablelessPlugChannelType(this.channelType)) return [];
    let cabinet = parent ? parent : this.room?.cabinet;
    // lan has special handling
    if (channelTypeInfo(this.channelType).isLan) {
      // special handling for LAN
      cabinet = undefined;
      // find cabinet for lan Actor
      for (const c of state.cabinets.asArray()) {
        // find the cabinet for the LAN actor
        if (c.actors.filter((actor) => actor.channelType == ChannelType.Lan).length > 0) {
          cabinet = c;
          break;
        }
      }
      if (!cabinet) {
        // if no cabinet with LAN actor is found, create a new cabinet for it
        cabinet = new Cabinet({}, state.cabinets);
        cabinet.tag = "P1"; // no itx
        cabinet.name = itx("toolBar.lan", "Patchfeld");
        state.cabinets.add(cabinet);
      }
    }
    // first check if cabinet is set
    if (!cabinet) {
      // check first if cabinet is in project
      if (state.cabinets.size > 0) {
        // set the first cabinet as cabinet from floor
        const floor = this.parentRoom?.parent as Floor | undefined;
        if (floor) floor.cabinetId = state.cabinets.asArray()[0].id;
        cabinet = this.room?.cabinet;
      } else {
        // no vabinet in project create a main cabinet and set it to floor
        cabinet = new Cabinet({}, state.cabinets);

        state.cabinets.add(cabinet);
        const floor = this.parentRoom?.parent as Floor | undefined;
        if (floor) floor.cabinetId = state.cabinets.asArray()[0].id;
      }
    }

    return cabinet?.autoConnectChannel(page, this, isDali === undefined ? this.isDali : isDali) || [];
  }

  raiseOnChange(changedProperty: string | any | undefined = undefined, eventSource?: string, uiOnly?: boolean) {
    super.raiseOnChange(changedProperty, eventSource, uiOnly);

    if (changedProperty != "selected") {
      const connetedChannel = this.getConnectedChannel();
      connetedChannel?.raiseOnChange("consumer", eventSource, uiOnly);

      if (changedProperty == "fuse" || changedProperty == "terminal" || changedProperty == "connections") {
        this.raiseOnChange("childCollection", eventSource, uiOnly);
      }
    }
  }

  getAllTagsFromSameTypeWithoutOwn(): Array<string> | undefined {
    if (this.parent && !state.projectMetadata.uniqueTagsForProject) {
      const res = new Array<string>();
      (this.parent as Room).consumers.forEach((consumer1) => {
        if (this.tag != consumer1.tag) {
          res.push(consumer1.tag);
        }
      });
      return res;
    } else {
      const res = new Array<string>();
      state.allConsumers().forEach((consumer1) => {
        if (this.tag != consumer1.tag) {
          res.push(consumer1.tag);
        }
      });
      return res;
    }
    return undefined;
  }

  //////////////////////////////////////////////////////
  ///////////// for copy
  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    const res = new Array<BaseModel>();
    this.connections.forEach((connection) => {
      res.push(connection);
    });
    return res;
  }
  //////////////////////////////////////////////////////
  ///////////// for paste
  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    return (await this.parent?.pasteItem(params)) || [];
  }

  async executePaste(params: executePaste_Parameters): Promise<Array<BaseModel>> {
    const { parent, isCopyRootObj } = params;
    const room = parent as Room;
    const page = room.page;
    var undoObjects: Array<BaseModel> = [this];

    // Set prefixType before the tag, because tag generation depends on it.
    this.updatePrefixTypeFromImage();
    if (isCopyRootObj) {
      room.addConsumer(this);
      if (page) {
        super.updatePageUsageAfterCopy();
      }
      this.updateNameInPaste(room.consumers);
      this.updateTagInPaste(room.consumers);
    } else if (state.projectMetadata.uniqueTagsForProject) {
      this.updateTagInPaste(room.consumers);
    }

    if (page) {
      //
      // connections
      this.connections.forEach((connection) => {
        connection.executePasteHelper({
          isCopyRootObj: isCopyRootObj,
          initiator: this,
          undoObjects: undoObjects,
          page: page,
        });
      });

      if (!this.getConnectedChannel()) {
        const createdObjects = await later(() => {
          return this.autoConnectChannel(page);
        }, 100);
        undoObjects.concat(createdObjects);
      }

      later(() => {
        this.connections.forEach((connection) => {
          connection.updateEndPoints();
        });
      });
    }
    return undoObjects;
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    if (obj.className == "Consumer") {
      const consumer = obj as Consumer;
      if (AllowedConections.isConnectionForTerminalViaConnectionsAllowed(consumer, this)) {
        const connectionToConsumer = this.setTerminalViaConsumer(consumer);
        if (connectionToConsumer) {
          return new DropResult(true, itx("Consumer.terminalViaSet", "Verbindung gesetzt"));
        }
      }
      return new DropResult(false, itx("Consumer.connectionForbidden", "Verbindung ist nicht erlaubt"));
    }

    if (obj.className == "Fuse") {
      const fuse = obj as Fuse;
      const connectedWithFuse = this.connectWithFuse(fuse);
      return new DropResult(connectedWithFuse, connectedWithFuse ? itx("Consumer.fuseSet") : itx("Consumer.noFuseSet"));
    }

    if (obj.className == "TerminalStrip") {
      const connectable = this.connectWithTerminalStrip(obj as TerminalStrip);
      if (connectable) {
        return new DropResult(true, itx("Consumer.terminalStripSet"));
      } else {
        return new DropResult(false, itx("Consumer.terminalStripSetNotAllowed", "Verbindung nicht erlaubt"));
      }
    }

    if (obj.className == "Terminal") {
      this.connectWithTerminal(obj as Terminal);
      return new DropResult(true, itx("Consumer.terminalSet"));
    }

    if (obj.className == "Scene") {
      const res = Connection.createConnection(obj as ConnectableObject, this);

      if (res.connection) {
        if (res.connection.type != ConnectionType.Scene) {
          logger.error("Consumer.handleDrop2: created connection is not of type Scene");
          res.connection.type = ConnectionType.Scene;

          const actor = this.getConnectedChannel()?.parent as Actor;
          if (actor.template) {
            actor.reEvalKnxMetadataNeeded = true;
            actor.updateAllFromKnxMetadata();
          }

          return new DropResult(true, res.msg);
        }
      }

      return new DropResult(false, itx("Consumer.connectionForbidden", "Verbindung ist nicht erlaubt"));
    }

    if (
      obj.className == "Channel" ||
      obj.className == "ButtonChannel" ||
      obj.className == "Scene" ||
      obj.className == "Button"
    ) {
      let isConnectable = true;
      const target = this;
      let source = obj as ConnectableObject;

      if (source.className == "Button") {
        const buttonChannel = (obj as Button).findConnectableChannel(ConnectionType.Button, this);
        if (buttonChannel) {
          source = buttonChannel;
        } else {
          return new DropResult(false, itx("Consumer.connectionForbidden", "Verbindung ist nicht erlaubt"));
        }
      }

      const connectionType = Connection.getConnectionType(source, target);
      isConnectable = AllowedConnections.isConnectionAllowed(obj as ConnectableObject, this, true);

      if (isConnectable) {
        const res = Connection.createConnection(obj as ConnectableObject, this);

        if (res.connection) {
          return new DropResult(true, res.msg);
        }
      }
      return new DropResult(false, itx("Consumer.connectionForbidden", "Verbindung ist nicht erlaubt"));
    }

    if (obj.className == "Actor") {
      let actor = obj as Actor;
      const channel = actor.findConnectableChannel(ConnectionType.Channel, this, "soft");
      if (channel) {
        this.handleDrop2(mode, channel);
      } else if (actor.channelType != this.channelType) {
        // ask if channelType should be changed
        const ok = await estadaConfirm(
          itx("consumer.changeChannelType", "Channel Type ändern"),
          itx(
            "consumer.changeChannelTypeFromT1ToT2",
            `willst du wirklich den ChannelType von ${this.channelType} auf ${actor.channelType} ändern?`,
            { T1: itx(`channelType.${this.channelType}`), T2: itx(`channelType.${actor.channelType}`) },
          ),
        );
        if (ok) {
          this.changeChannelType(actor.channelType);

          if (this.getConnectedChannel()?.parent?.id != actor.id) {
            const channel = actor.findConnectableChannel(ConnectionType.Channel, this, "soft");
            if (channel) {
              return channel.treeViewModel.handleDrop2(mode, this);
            }
          } else {
            return new DropResult(true, itx("Consumer.channelTypeChanged"));
          }
        }
      }
    }

    if (obj.className == "CableType") {
      let cableType = obj as CableType;
      this.cableType = cableType;
      this.raiseOnChange("cableType");
      cableType.raiseOnChange("childCollection");
      return new DropResult(true, itx("Consumer.cableTypeSet"));
    }
    return new DropResult(false);
  }

  connectWithTerminalStrip(terminalStrip: TerminalStrip): boolean {
    if (terminalStrip.voltageLevel != this.wireRequirement.voltage) {
      return false;
    }
    if (this.getTerminalViaConsumer()) {
      return false;
    }
    if (this.terminal?.parent == terminalStrip) {
      // consumer is allready connected with this terminalstrip
      return false;
    }

    //
    // append new terminal to terminalstrip
    //

    const newTerminal = new Terminal({}, terminalStrip);
    terminalStrip.terminals.add(newTerminal);
    newTerminal.tag = state.nameAndTagManager.generateTag(newTerminal);

    ///////////////////////////////////////////////////////////////////////////////////
    // set terminal size according to cableType or wireRequirement
    ///////////////////////////////////////////////////////////////////////////////////

    let numberOfWiresWithoutPEandN =
      this.cableType?.numberOfWiresWithoutPEandN ||
      this.wireRequirement.numberOfLWiresWithoutPEandN ||
      state.projectMetadata.defaultNumberOfWiresPerCable - 2; // Hier unterstellen wir, das hier immer Kabel mit PE und N gemeint sind !!!!

    newTerminal.getTerminalBlock()?.assignSizeFromCableSizeWithoutPEandN(numberOfWiresWithoutPEandN);

    ///////////////////////////////////////////////////////////////////////////////////

    this.connectWithTerminal(newTerminal);

    terminalStrip.raiseOnChange("childCollection");
    return true;
  }

  connectWithTerminal(
    terminal: Terminal | undefined,
    errorsToIgnore?: Array<string>,
  ): { connected: boolean; errortext?: string } {
    let errortext: string | undefined = undefined;
    let connected = false;
    ////////////////////////////////////////////////////////////////////////////////////////
    // check first if  terminal has allready a consumer
    if (terminal && terminal.consumer) {
      return {
        connected: false,
        errortext: itx("Consumer.allreadyConnected", "Klemme ist bereits mit einer anderen Funktion verbunden"),
      };
    }
    /////////////////////////////////////////////////////////////////////////////////////////
    ////////////////////////////////////////////////////////////////////////////////////////
    // check first if consumer and terminal are in same cabinet
    const cabinet = this.getConnectedChannel()?.parent?.parent;
    if (
      terminal &&
      cabinet &&
      terminal?.parent?.parent != cabinet &&
      !errorsToIgnore?.includes("differentCabinet") &&
      this.getConnectedChannel()
    ) {
      return {
        connected: false,
        errortext: itx(
          "Consumer.differentCabinet",
          "Verbraucher ist in einem anderen Schaltschrank als die zu verbindende Klemme",
        ),
      };
    }
    /////////////////////////////////////////////////////////////////////////////////////////
    // check now if consumer has a cable
    if (this.noCable) {
      return {
        connected: false,
        errortext: itx("Consumer.noCable", "Dieser Verbrauchertype unterstützt keinen Terminalanschluss"),
      };
    }
    // terminal is master terminal or undefined
    if (terminal?.isMasterTerminal) {
      ////////////////////////////////////////////////////////////////////////////////////////
      // if terminal is master terminal, we can connect like before
      ////////////////////////////////////////////////////////////////////////////////////////
      const lastTerminal = this.terminal;

      if (this.getAllViaConsumers().length > 0) {
        //
        // wenn der Consumer ein Unterverteiler ist,
        // dann expandierten wir das Terminal hier,
        // damit die subTerminals existieren, wenn updateRelastions versucht die ingehenden viaConsumer zu verbinden.
        //
        terminal.getTerminalBlock()?.splitGroupTerminalIntoSingleTerminals();
      }

      this.terminal = terminal;

      this.raiseOnChange("terminal");

      terminal?.raiseOnChange("consumer");
      lastTerminal?.raiseOnChange("consumer");

      connected = true;
    } else if (terminal) {
      ////////////////////////////////////////////////////////////////////////////////////////
      // if terminal is subterminal
      ////////////////////////////////////////////////////////////////////////////////////////

      // here terminal is subterminal, via Connection of consumer has to be set here
      const masterTerminal = terminal.masterTerminal;
      const viaConsumer = masterTerminal.consumer;
      const consumer = this;

      // check first if viaConsumer is the same as consumer
      if (viaConsumer == consumer) {
        return {
          connected: false,
          errortext: itx("Consumer.consumerAllreadyOnTerminalBlock", "Funktion ist schon auf diesem Klemmblock"),
        };
      }

      /// check here if consumer has allready a via connection
      if (consumer.getTerminalViaConsumer()) {
        return {
          connected: false,
          errortext: itx("Consumer.consumerAllreadyHasViaConnection", "Funktion hat bereits eine Via-Verbindung"),
        };
      }

      const neededNumberOfWires = this.wireRequirement?.numberOfLWiresWithoutPEandN || 1;
      const freeWireIndexs = viaConsumer?.findFreeWireIndexs(neededNumberOfWires, consumer) ?? [];
      const terminalWireIndex = masterTerminal.getTerminalBlock()?.indexOfTerminal(terminal); // without PE and N
      const hasGround = viaConsumer?.cableType?.hasGround ?? true;
      const fullWireIndex = (terminalWireIndex || 0) + (hasGround ? 2 : 1);

      // check first if terminal is free for function
      if (terminalWireIndex !== undefined && terminalWireIndex >= 0 && freeWireIndexs.includes(fullWireIndex) && viaConsumer) {
        if (AllowedConections.isConnectionForTerminalViaConnectionsAllowed(consumer, viaConsumer)) {
          consumer.setTerminalViaConsumer(viaConsumer);
          consumer.terminalViaWireIndex = fullWireIndex;
          consumer.raiseOnChange("TerminalVia");
          connected = true;
          errortext = itx("Consumer.terminalViaSet", "Via Verbindung wurde gesetzt");
        } else {
          // connection not allowed
          errortext = itx("Consumer.connectionForbidden", "Verbindung ist nicht erlaubt");
        }
      } else {
        errortext = itx(
          "Consumer.terminalHasNoAvailableViaConsumer",
          "Diese Klemme ist für diese Funktion nicht verfügbar",
        );
      }
    } else {
      ////////////////////////////////////////////////////////////////////////////////////////
      // here terminal is undefined, so via connection has to be deleted
      ////////////////////////////////////////////////////////////////////////////////////////

      const oldTerminal = this.terminal;
      if (oldTerminal?.isMasterTerminal) {
        this.terminal = undefined;
      }
      oldTerminal?.raiseOnChange("consumer");
      this.raiseOnChange("TerminalVia");
      this.raiseOnChange("terminal");
      connected = true;
    }
    return { connected: connected, errortext: errortext };
  }

  connectWithFuse(fuse: Fuse | undefined): boolean {
    // check first if fuse and consumer are in same cabinet -> for decentral actor too
    const consumerCabinet = this.getConnectedChannel()?.parent?.parent;
    const fuseCabinet = fuse?.parent?.className == "Cabinet" ? fuse?.parent : fuse?.parent?.parent;
    if (fuse && fuseCabinet != consumerCabinet) {
      return false;
    }

    // check now if consumer has a cable
    if (fuse && this.noCable) {
      return false;
    }

    /// here set Tag here
    if (this.channelType == ChannelType.suply400V && fuse && fuse.getSize() != 3) {
      let calcTag: string;
      if (fuse.parent?.className == noItx("ELCB") && fuse.tag.startsWith(`${fuse.parent.tag}.`)) {
        // fuse under an elcb: keep the "<elcbTag>." prefix and expand the local index to a 3-pole range
        const elcbTag = fuse.parent.tag;
        const localPart = fuse.tag.startsWith(`${elcbTag}.`) ? fuse.tag.substring(elcbTag.length + 1) : fuse.tag;
        const localNumber = parseInt(localPart.replace(/^\D+/g, ""));
        calcTag = `${elcbTag}.${localNumber}-${localNumber + 2}`;
      } else {
        const numbers = fuse.tag.replace(/^\D+/g, "");
        const prefix = state.settings.devicePrefix.fuse;
        calcTag = `${prefix}${numbers}-${prefix}${parseInt(numbers) + 2}`;
      }

      fuse.tag = calcTag;
    }
    const oldFuse = this.fuse;
    this.fuse = fuse;
    this.raiseOnChange("fuse");
    this.fuse?.raiseOnChange();
    oldFuse?.raiseOnChange();
    return true;
  }

  connectWithButtonChannel(buttonChannel: ButtonChannel) {
    const currentDrawingConnection = state.currentDrawingConnection;
    const room = this.parentRoom;
    if (room) {
      const page1 = room?.getOrCreatePage();

      const c = new Connection({}, page1);
      page1.connections.add(c);

      c.fromDevice = this;
      c.toDevice = buttonChannel;
      c.type = ConnectionType.Button;
      currentDrawingConnection.connect(buttonChannel, ConnectionType.Button);

      buttonChannel.raiseOnChange();
      this.raiseOnChange();
    }
  }

  //////////////////////////////////////////////////////

  generateNewNameFromTag() {
    let name = "";
    const tag = this.tag;
    var thenum = tag.replace(/^\D+/g, "");
    let before = "";
    if (channelTypeInfo(this.channelType).isLight) {
      before = itx("components.light");
    } else {
      before = itx("channelType." + this.channelType);
    }

    name = before + " " + thenum;
    this.name = name;
  }

  //////////////////////////////////////////////////////
  ///////////// for paste
  mapAfterCopy() {
    if (!this.mapAfterCopyDone) {
      super.mapAfterCopy();

      this.fuseId = undefined; //  state.objectMapping.getMappedId(this.fuseId);
      this.terminalId = undefined; //  = state.objectMapping.getMappedId(this.terminalId);
    }
  }

  setCableTypeByName(name: string) {
    let cableType = state.cableTypes.getItemByName(name);
    function calcNumberOfWires(name: string): number {
      const ss = name.toLocaleLowerCase().split("x");
      if (ss.length == 2) {
        const s = ss[0].trim();
        if (`${parseInt(s)}` == s) {
          return parseInt(s);
        }
      }
      return 0;
    }
    if (!cableType) {
      cableType = new CableType({ name: name, tag: `C${state.cableTypes.items.size + 1}` }, state.cableTypes);
      state.cableTypes.addWithoutUpdate(cableType);
    }
    if (cableType?.numberOfWires == 0 || cableType.numberOfWires == undefined) {
      cableType.numberOfWires = calcNumberOfWires(name);
    }
    this.cableType = cableType;
    this.raiseOnChange("cableType");
  }

  get viaTerminal(): Terminal | undefined {
    return this.getTerminalViaConsumer()?.terminal;
  }

  static getSignalByType(obj: Consumer | Multiplexer, type: SignalType): Signal | undefined {
    var res: any = undefined;
    obj.signals.forEach((signal) => {
      if (signal.type.equalsExtended(type)) {
        res = signal;
      }
    });

    return res;
  }

  static getOrCreateSignalByType(obj: Consumer | Multiplexer, type: SignalType): Signal | undefined {
    var res = Consumer.getSignalByType(obj, type);
    if (!res) {
      const signal = new Signal({ type: type }, obj);
      obj.signals.addWithoutUpdate(signal);
      res = signal;
    }

    return res;
  }

  consistencyCheck(parent: BaseModel) {
    super.consistencyCheck(parent);
    this.signals?.consistencyCheck(this);

    const channelConnections = new Array<{ con: Connection; partner: ConnectableObject }>();
    this.connections.forEach((con) => {
      if (con.type == ConnectionType.Channel) {
        const partner = con.getPartner(this);
        if (partner) {
          channelConnections.push({ con: con, partner: partner });
        }
      }
    });
    if (channelConnections.length > 1) {
      channelConnections.forEach(({ con, partner }) => {
        this.logConsistencyError(
          itx("Consumer.deleteConnectionTo", "Lösche Verbindung zu: ") + partner.fullTag,
          "",
          () => {
            con.delete();
          },
        );
      });
    } else if (channelConnections.length == 0) {
      this.logConsistencyError(
        itx("Consumer.noChannelConnection", "Keine Aktor Verbindung vorhanden "),
        "noActorChannelConnection", // no itx
      );
    }

    // check if terminal connection is correct

    // check if noCable then no terminal is allowed
    if (this.noCable) {
      if (this.terminal) {
        this.terminal = undefined;
        this.logConsistencyError(
          itx("Consumer.noCableTerminalConnection", "Dieser Verbrauchertype unterstützt keinen Terminalanschluss."),
        );
      }
      if (this.fuse) {
        this.fuse = undefined;
        this.logConsistencyError(
          itx("Consumer.noCableFuseConnection", "Dieser Verbrauchertype unterstützt keinen Anschluss einer Sicherung."),
        );
      }
    }
    // terminal and fuse has to be in the same cabinet as consumer
    const consumerCabinet = this.getConnectedChannel()?.parent?.parent;
    const terminalCabinet = this.terminal?.parent?.parent;
    const fuseCabinet = this.fuse?.parent?.className == "Cabinet" ? this.fuse?.parent : this.fuse?.parent?.parent;
    if (this.terminal && consumerCabinet != terminalCabinet) {
      this.logConsistencyError(
        itx(
          "Consumer.terminalDifferentCabinet",
          "Der verbundene Terminal ist in einem anderen Schaltschrank. Verbindung löschen.",
        ),
        noItx("T1TerminalsDifferentCabinet"),
        () => {
          this.connectWithTerminal(undefined);
        },
      );
    }
    if (this.fuse && fuseCabinet != consumerCabinet) {
      this.logConsistencyError(
        itx(
          "Consumer.fuseDifferentCabinet",
          "Die verbundene Sicherung ist in einem anderen Schaltschrank. Verbindung löschen.",
        ),
        noItx("fuseDifferentCabinet"),
        () => {
          this.connectWithFuse(undefined);
        },
      );
    }

    // check heating consumer actor has one temperature_current button signal
    // Suche alle IstTemperatur Aktor-Signale die mit mehreren Button IstTemperatur Signalen verbunden sind

    const channel = this.getConnectedChannel();
    if (
      channel?.isActorChannel &&
      this.channelType == ChannelType.Heating &&
      this.signals.asArray().filter((s) => s.groupAddresses.length > 0).length > 0
    ) {
      for (const signal of channel.signals) {
        if (signal.type == SignalTypes.Temperature_Current) {
          // check here if signal is connectet with one button signal of type Temperature_Current
          let numberOfButtonCurrentTemperatureSignals = 0;
          for (const ga of signal.groupAddresses) {
            for (const signal2 of ga.getSignalBackpointers(true)) {
              if ((signal2.parent as Channel)?.isButtonChannel && signal2.type == SignalTypes.Temperature_Current) {
                numberOfButtonCurrentTemperatureSignals++;
              }
            }
          }
          if (numberOfButtonCurrentTemperatureSignals > 1) {
            this.flagErrors(
              {
                tempCurrentMultipleButtonUsages: "",
              },
              true,
            );
            this.logConsistencyError(
              itx(
                "errors.tempCurrentMultipleButtonUsages",
                "Dieses IstTemperatur Aktor-Signal ist mit mehreren IstTemperatur Button-Signalen verbunden.",
              ),
              noItx("tempCurrentMultipleButtonUsages"),
            );
            this.flagErrors(
              {
                tempCurrentnoButtonUsage: "",
              },
              false,
            );
          } else if (numberOfButtonCurrentTemperatureSignals == 0) {
            this.flagErrors(
              {
                tempCurrentnoButtonUsage: "",
              },
              true,
            );
            this.logConsistencyError(
              itx(
                "errors.tempCurrentnoButtonUsage",
                "Dieses IstTemperatur Aktor-Signal ist mit keinem IstTemperatur Button-Signal verbunden.",
              ),
              noItx("tempCurrentnoButtonUsage"),
            );
            this.flagErrors(
              {
                tempCurrentMultipleButtonUsages: "",
              },
              false,
            );
          } else {
            this.flagErrors(
              {
                tempCurrentMultipleButtonUsages: "",
                tempCurrentnoButtonUsage: "",
              },
              false,
            );
          }
        }
      }
    }
  }

  get defaultImageSrc(): string {
    return this.image;
  }

  drawingItemColor(): string {
    const drawingItemColors = state.settings.drawingItemColors;
    let color = drawingItemColors.consumer || "rgb(158, 211, 109)";

    const info = channelTypeInfo(this.channelType);
    if (info.isLight) {
      color = drawingItemColors.light;
    } else if (info.isRollo) {
      color = drawingItemColors.jalousie;
    } else if (info.isHeating) {
      color = drawingItemColors.heating;
    } else if (info.isSupply) {
      color = drawingItemColors.supply;
    } else if (this.channelType == ChannelType.Lock) {
      color = drawingItemColors.lock;
    }

    return color;
  }

  onConnect(connection: Connection): void {
    if (connection.type == ConnectionType.TerminalVia) {
      if (connection.fromDevice == this) {
        //
        // ausgehende viaConnection von diesem Consumer
        //
        this.updateTerminalRelationOverViaConsumer();
      }
    }
  }

  onDisconnect(connection: Connection): void {
    if (connection.type == ConnectionType.TerminalVia) {
      if (connection.fromDevice == this) {
        //
        // disconnect outgoing viaConnection
        //
        if (this.terminal) {
          this.terminal = undefined;
        }
      }
    }
  }

  getAllViaConsumers(): Array<Consumer> {
    let res = new Array<Consumer>();
    state.allConsumers().forEach((consumer) => {
      if (this == consumer.getTerminalViaConsumer()) {
        res.push(consumer);
      }
    });
    res = sortArrayByIntProperty(res, "terminalViaWireIndex");
    return res;
  }

  //
  // returns frist free wireIndex
  // wireIndex starts with
  //  # 0 for yellow/green - PE
  //  # 1 for blue         - mainTerminal, N
  //  # 2 brown            - mainTerminal, P

  findFreeWireIndexs(neededNumberOfWires: number, withoutConsumer?: Consumer): Array<number> {
    const res = new Array<number>();
    const cableType = this.cableType;
    const distribution = this.channelType === ChannelType.IntermediateDistribution;
    if (cableType || distribution) {
      const allViaConsumer = this.getAllViaConsumers();

      const cableHasGround = cableType?.hasGround ?? true;

      const usedIndexes = new Array<number>();

      //
      // durch diesen Consumer selbst belegete Adern sperren
      //
      let startSelf = 2;
      if (!cableHasGround) {
        startSelf--;
      }

      // A distribution only passes conductors through; it consumes none itself.
      const ownWireCount = distribution
        ? 0 : this.wireRequirement?.numberOfLWiresWithoutPEandN || 1;
      for (let i = startSelf; i < startSelf + ownWireCount; i++) {
        usedIndexes.push(i);
      }

      //////////////////////////////////////////////////////////////

      //
      // durch eingehenende Consumer bereits belegte Adern sperren
      //

      allViaConsumer.forEach((consumer) => {
        if (consumer == withoutConsumer) {
          return;
        }
        if (consumer.terminalViaWireIndex != undefined) {
          for (let i = 0; i < (consumer.wireRequirement.numberOfLWiresWithoutPEandN || 1); i++) {
            usedIndexes.push(consumer.terminalViaWireIndex + i);
          }
        }
      });

      const sortedUsedWireIndexes = usedIndexes.sort();
      ////////////////////////////////////////////////////////////////

      //
      // Jetzt freie Adern suchen
      //

      // Without a cable, defer capacity validation but still choose the first
      // unoccupied conductor instead of always assigning black (index 3).
      const numberOfWiresOfThisCable = cableType?.numberOfWires
        || (distribution && !cableType ? Math.max(2, ...usedIndexes.map(index => index + 1)) + neededNumberOfWires : 3);

      let start = 2;
      if (!cableHasGround) {
        start--;
      }

      for (let wireIndex = start; wireIndex + neededNumberOfWires <= numberOfWiresOfThisCable; wireIndex++) {
        let found = true;
        for (let i = 0; i < neededNumberOfWires; i++) {
          if (sortedUsedWireIndexes.includes(wireIndex + i)) {
            found = false;
            break;
          }
        }

        if (found) {
          res.push(wireIndex);
        }
      }
    } else {
      res.push(3); // if no cableType is set, just return 3
    }

    return res;
  }

  get connectedObjectsInOtherRooms(): Array<Button | Consumer> {
    const res = new Array<Button | Consumer>();
    const ownRoom = this.room;

    if (ownRoom) {
      this.connections.forEach((con) => {
        if (con.type == ConnectionType.Button || con.type == ConnectionType.TerminalVia) {
          const partner = con.getPartner(this);

          if (partner?.isConsumer) {
            const partnerRoom = (partner as Consumer).room;
            if (partnerRoom && partnerRoom != ownRoom) {
              res.push(partner as Consumer);
            }
          }

          if (partner?.isButtonChannel) {
            const partnerRoom = (partner as ButtonChannel).room;
            if (partnerRoom && partnerRoom != ownRoom && partner.parent) {
              res.push(partner.parent as Button);
            }
          }
        }
      });
    }

    return res;
  }

  get isPlugSwitchable(): boolean {
    return isSwitchedPlugChannelType(this.channelType);
  }
}
